import {
    ActionReducerMapBuilder,
    Draft,
    SerializedError,
    createAsyncThunk,
    createSlice,
} from '@reduxjs/toolkit';
import { IAction, SliceProps } from '../store';
import { urlParamReplicaId, urlProjectDocumentsDelete } from '../../Constants';
import { apiFetch } from '../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IDeleteImportDocumentsState = {
    isDialogOpen: false,
    isDeletionCompleted: false,
    documentsNotDeleted: null,
    loading: false,
    error: null,
};

const reducers = {
    openDeleteImportDocumentsDialog(state: IDeleteImportDocumentsState) {
        state.isDialogOpen = true;
        state.documentsNotDeleted = null;
        state.isDeletionCompleted = false;
    },

    closeDeleteImportDocumentsDialog(state: IDeleteImportDocumentsState) {
        state.isDialogOpen = false;
        state.isDeletionCompleted = false;

        state.loading = false;
        state.error = null;
    },
};
/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    onConfirmDeleteDocuments: async (config: {
        replicaId: string;
        documentIds: string[];
    }): Promise<IErrorDeleteDocumentData[]> => {
        const { replicaId, documentIds } = config;

        try {
            const url = urlProjectDocumentsDelete.replace(urlParamReplicaId, replicaId);

            const formData = new FormData();
            formData.append('universalIds', documentIds.join('|'));

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };

            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IDeleteImportDocumentsState>) => {
        /**
         * Reducers for the 'onConfirmDeleteDocuments' thunk.
         */
        builder.addCase(
            onConfirmDeleteDocuments.pending,
            (state: Draft<IDeleteImportDocumentsState>) => {
                state.loading = true;
                state.error = null;
            },
        );

        builder.addCase(
            onConfirmDeleteDocuments.fulfilled,
            (
                state: Draft<IDeleteImportDocumentsState>,
                action: IAction<IErrorDeleteDocumentData[]>,
            ) => {
                state.isDeletionCompleted = true;
                state.documentsNotDeleted = action.payload;

                state.loading = false;
                state.error = null;
            },
        );

        builder.addCase(
            onConfirmDeleteDocuments.rejected,
            (state: Draft<IDeleteImportDocumentsState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};

export const onConfirmDeleteDocuments = createAsyncThunk(
    `${urlProjectDocumentsDelete}`,
    thunks.onConfirmDeleteDocuments,
);

export interface IErrorDeleteDocumentData {
    universalId: string;
    error: string;
}

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IDeleteImportDocumentsState extends SliceProps {
    isDialogOpen: boolean;
    isDeletionCompleted: boolean;
    documentsNotDeleted: IErrorDeleteDocumentData[];
}

/**
 * Create the redux slice.
 */
export const deleteImportDocumentsDataSlice = createSlice({
    name: 'deleteImportDocumentsData',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export functions from slice actions.
 */
export const { openDeleteImportDocumentsDialog, closeDeleteImportDocumentsDialog } =
    deleteImportDocumentsDataSlice.actions;

/**
 * Export slice reducer as default.
 */
export default deleteImportDocumentsDataSlice.reducer;
